import { observable, action, set, runInAction, computed } from 'mobx';
import { dispatch } from 'rfx-core';
import _ from 'lodash';
import { Parser } from 'json2csv';

import BaseUploader, {
  KnownFieldConfig,
} from '#/shared/stores/ui/BaseUploader';
import { getStore } from '#/shared/getStores';
import { IS_PRODUCTION } from '#/config/settings';

export default class PaymentAdjustmentsUploader extends BaseUploader {
  constructor() {
    super({ title: 'ui.PaymentAdjustmentsUploader' });

    set(this.saveResults, {
      ignored: [],
      invalid: [],
      updated: [],
    });

    return this;
  }

  @observable parseOptions = {
    dynamicTyping: false,
    header: false,
    skipEmptyLines: true,
  };

  @observable
  list = [];

  @observable
  parsedHeaders = [];

  @observable
  hasErrors = false;

  @computed
  get knownFields(): KnownFieldConfig[] {
    return [
      { key: 'assignmentUUID' },
      { key: 'companyUUID' },
      { key: 'partnerUUID' },
      { key: 'additionalAmount' },
      { key: 'transactionNote' },
    ];
  }

  @computed get knownHeaders() {
    return _.map(this.knownFields, 'key');
  }

  @observable
  includeHowTo = false;

  @computed
  get csvData() {
    return this.$sampleCSVData;
  }

  @action
  async setup(opts, ...rest) {
    super.setup(opts, ...rest);

    this.clear();

    this.company = opts.company;

    this.status = 'pending';

    if (_.isString(this.company)) {
      const company = await dispatch('companies.get', this.company, {
        select: false,
      });
      runInAction(() => {
        this.company = company;
      });
    }
  }

  @action
  clear() {
    super.clear();

    this.setStatus('pending');

    this.list.clear();
    this.parsedHeaders.clear();
  }

  @action
  async processFile({ options = this.parseOptions, ...rest }) {
    this.setStatus('loading');

    runInAction(() => {
      this.hasErrors = false;
    });

    try {
      await super.processFile({ options, ...rest });

      this.setStatus('loaded');
    } catch (err) {
      this.log.error('Failed to process file', err);

      this.setStatus('error');

      throw err;
    }
  }

  @action
  async parse({ rows }) {
    let parsedHeaders = [];
    const casedHeaders = _.map(rows[0], _.toLower);
    const indices = _.reduce(
      this.knownHeaders,
      (acc, header) => {
        acc[header] = _.indexOf(casedHeaders, _.toLower(header));
        return acc;
      },
      {},
    );

    const validRows = _(rows)
      .map((row, i) => {
        if (!i) return null;

        this.log.debug(row);
        return _.reduce(
          this.knownHeaders,
          (acc, colKey) => {
            const colIndex = indices[colKey];

            if (colIndex >= 0) {
              acc[colKey] = row[colIndex];
            }

            return acc;
          },
          {},
        );
      })
      .reject((rowObj) => _.isEmpty(_.omitBy(rowObj, _.isEmpty)))
      .map((row) => {
        parsedHeaders = _.union(parsedHeaders, _.keys(row));
        return row;
      })
      .map((row) => {
        if (
          !row.assignmentUUID ||
          !row.additionalAmount ||
          !row.companyUUID ||
          row.additionalAmount === '0'
        ) {
          _.set(
            row,
            'err',
            'Missing required assignmentUUID, companyUUID, or additionalAmount value',
          );
          runInAction(() => {
            this.hasErrors = true;
          });
        }
        if (Number(row.additionalAmount) > 100) {
          _.set(row, 'err', 'additionalAmount value too high');
          runInAction(() => {
            this.hasErrors = true;
          });
        }

        return row;
      })
      .compact()
      .value();

    this.parsedHeaders = parsedHeaders;

    runInAction(() => {
      this.list.replace(validRows);
    });
  }

  @action
  async save() {
    this.setStatus('saving');

    const invalidEntries = this.list.filter((entry) => entry.err);

    if (!_.isEmpty(invalidEntries)) {
      runInAction(() => {
        this.saveResults.invalid.push(...invalidEntries);
      });
      this.setStatus('Error Saving Results');
      this.setSaving(false);
      this.setErrorMessage('Payments to be saved contain errors.');
      return;
    }

    const parser = new Parser({
      fields: Object.keys(this.list[0]),
    });
    const csvData = parser.parse(this.list);

    const file = new Blob([csvData], { type: 'text/csv' });

    const filename = `${
      this.company.uuid
    }/payment-adjustments/${new Date().getTime()}`;
    const bucketService = 'CLOUD_STORAGE_SCHEDULE';

    const query = {
      action: 'sign',
      bucketService,
      contentType: file.type,
      filename,
      operationName: 'putObject',
      useStagingClient: !IS_PRODUCTION,
    };

    const res = await getStore('gcp').get(null, { query });

    await fetch(res.signedUrl, {
      body: file,
      headers: { 'Content-Type': file.type },
      method: 'PUT',
    });

    try {
      const response = await getStore('paymentAdjustmentUploader').create({
        data: {
          bucketService,
          companyId: this.company.uuid,
          companyName: this.company.name,
          filename,
          useStagingClient: !IS_PRODUCTION,
        },
      });
      if (response.success) {
        this.setStatus('saved');
      }
    } catch (err) {
      this.log.error(`Failed to save payment records`, err);
      dispatch('ui.snackBar.error', 'Sorry, something went wrong');
      this.setStatus('error');
    }
  }
}
